/* © Facilogi. See COPYRIGHT file for full copyright & licensing details. */

import { $t } from "App/IOC";

import {
    NewsletterFrequency, NewsletterSearch, NewsletterSend
} from "App/Models/Newsletter";

/* Ce qui se dit pareil dans les trois fenetres de la newsletter ( liste, reglage,
   statistiques ). */

export const WEEKDAYS: number[] = [ 1, 2, 3, 4, 5, 6, 7 ];

export function WeekdayName( weekday: number ): string {
    return $t( `NEWSLETTERS_weekday${ weekday }` ).toString();
}

/* « Le lundi, toutes les semaines, a 09:00 » / « Le premier lundi, tous les 2 mois,
   a 09:00 ». */
export function DescribeFrequency( frequency: NewsletterFrequency | undefined ): string {
    if( frequency === undefined || frequency === null )
        return "";

    const day = WeekdayName( frequency.weekday ).toLowerCase();
    const every = Number( frequency.every ) || 1;

    if( frequency.kind === "monthly_first" )
        return ( every === 1 )
            ? $t( "NEWSLETTERS_frequencyMonthly", { day, time: frequency.time } ).toString()
            : $t( "NEWSLETTERS_frequencyMonthlyN", {
                day, every, time: frequency.time
            } ).toString();

    return ( every === 1 )
        ? $t( "NEWSLETTERS_frequencyWeekly", { day, time: frequency.time } ).toString()
        : $t( "NEWSLETTERS_frequencyWeeklyN", { day, every, time: frequency.time } ).toString();
}

/* Une date de l'API ( ISO 8601 avec fuseau ) en clair, a l'heure du poste. */
export function FormatDate( value: string | null | undefined, withTime: boolean = true ): string {
    if( value === undefined || value === null || value === "" )
        return "—";

    const date = new Date( value );

    if( isNaN( date.getTime() ) )
        return value;

    return withTime
        ? date.toLocaleString( "fr-FR", { dateStyle: "medium", timeStyle: "short" } as any )
        : date.toLocaleDateString( "fr-FR", { dateStyle: "medium" } as any );
}

/* « 35 974 » : les milliers separes, comme Insyder ( Fred, 22/10/2026 ). */
export function FormatNumber( value: number | null | undefined ): string {
    if( value === undefined || value === null )
        return "—";

    return Math.round( value ).toLocaleString( "fr-FR" );
}

export function FormatRate( value: number | null | undefined ): string {
    if( value === undefined || value === null )
        return "—";

    return `${ ( Math.round( value * 10 ) / 10 ).toLocaleString( "fr-FR" ) } %`;
}

export function SendStatusLabel( send: NewsletterSend ): string {
    return $t( `NEWSLETTERS_status_${ send.status }` ).toString();
}

/* Une selection vide : personne. */
export function HasInclusion( search: NewsletterSearch ): boolean {
    return search.all_contacts === true ||
        search.active_buyers === true ||
        search.active_renters === true ||
        search.owners_also_buyers === true ||
        ( search.tags_in || [] ).length > 0 ||
        ( search.contacts_in || [] ).length > 0 ||
        ( search.roles_in || [] ).length > 0;
}

/* La valeur d'un `datetime-local` ( « 2026-10-23T09:00 » ) depuis une date ISO, a
   l'heure du poste. */
export function ToLocalInput( value: string | null | undefined ): string {
    if( value === undefined || value === null || value === "" )
        return "";

    const date = new Date( value );

    if( isNaN( date.getTime() ) )
        return "";

    const pad = ( part: number ): string => String( part ).padStart( 2, "0" );

    return `${ date.getFullYear() }-${ pad( date.getMonth() + 1 ) }-${ pad( date.getDate() ) }` +
        `T${ pad( date.getHours() ) }:${ pad( date.getMinutes() ) }`;
}

/* Et l'inverse : la date ISO, fuseau compris, que l'API attend. */
export function FromLocalInput( value: string ): string {
    if( value === "" )
        return "";

    const date = new Date( value );

    return isNaN( date.getTime() ) ? "" : date.toISOString();
}

/* Le message d'une erreur de l'API, traduit quand on connait son code. */
/* Les codes d'erreur de l'API que l'ecran sait dire ; les autres sont montres tels
   quels ( sans demander une traduction qui n'existe pas ). */
const KNOWN_ERRORS = [
    "no_dynamic_block", "newsletter_not_found", "contact_not_found",
    "newsletter_without_sender", "no_article", "invalid_newsletter", "invalid_search",
    "newsletter_not_enabled", "route_not_allowed",
    /* La mediatheque du client ( reponse du back du 28/10/2026 ). */
    "image_required", "image_invalid", "name_required", "name_too_long",
    "public_media_not_found", "group_without_user"
];

export function ErrorMessage( code: unknown, detail?: string ): string {
    if( KNOWN_ERRORS.indexOf( String( code ) ) >= 0 )
        return $t( `NEWSLETTERS_error_${ String( code ) }` ).toString();

    return [ String( code ), detail ]
        .filter( ( part: string | undefined ): boolean => part !== undefined && part !== "" )
        .join( " — " );
}

/* POURQUOI RIEN NE PARTIRAIT ( Fred, 27/10/2026 : « je n'ai pas cree l'e-mail de
   la NL auto », l'apercu disait seulement « aucun article ni annonce » ). Lu dans
   l'e-mail lui-meme : le back ignore sans rien dire un bloc Articles sans blog ou
   a 0 article ( `NewsletterArticleBlock::fromContentEditor` ).
   - "NoEmail" : pas d'e-mail, ou ni bloc Articles ni bloc Annonces ;
   - "NoBlog"  : des blocs Articles, aucun relie a un blog avec au moins 1 article ;
   - ""        : l'e-mail est pret, il n'y a simplement rien de neuf. */
export function EmptyReason( contentEditor: unknown ): "NoEmail" | "NoBlog" | "" {
    let document: any = null;

    if( typeof contentEditor === "string" && contentEditor !== "" ) {
        try {
            document = JSON.parse( contentEditor );
        } catch( e ) {
            document = null;
        }
    }

    if( document === null || typeof document !== "object" )
        return "NoEmail";

    let articles = 0;
    let linked = 0;
    let listings = 0;

    const count = ( value: any ): number => {
        const raw = ( value !== null && typeof value === "object" ) ? value.Id : value;

        return Number( raw ) || 0;
    };

    const walk = ( node: any ): void => {
        if( node === null || typeof node !== "object" )
            return;

        if( node.Attributes !== undefined && node.Attributes !== null &&
            node.Attributes.Listings !== undefined && node.Attributes.Listings !== null )
            listings++;

        if( node.Blueprint === "BlogPostSection" && node.Content !== undefined &&
            node.Content !== null && node.Content.Post !== undefined &&
            node.Content.Post !== null ) {
            const post = node.Content.Post;

            articles++;

            if( Array.isArray( post.Blogs ) && post.Blogs.length > 0 && count( post.Total ) > 0 )
                linked++;

            return;
        }

        Object.keys( node ).forEach( ( key: string ): void => walk( node[ key ] ) );
    };

    walk( document );

    if( articles === 0 && listings === 0 )
        return "NoEmail";

    if( linked === 0 && listings === 0 )
        return "NoBlog";

    return "";
}

/* Le prochain envoi que donne le rythme, a partir de maintenant ( Fred, 21/10/2026 :
   « le prochain envoi est obligatoire » — a la creation, il est pre-rempli ).
   `weekday` : 1 = lundi ... 7 = dimanche. « Le premier » = le premier jour donne du
   mois, ce mois-ci s'il n'est pas passe, sinon le mois suivant. */
export function NextOccurrence(
    kind: string, weekday: number, time: string, from: Date = new Date()
): Date {
    const [ hours, minutes ] = ( time || "09:00" ).split( ":" )
        .map( ( part: string ): number => Number( part ) || 0 );
    const at = ( date: Date ): Date => {
        const result = new Date( date.getTime() );

        result.setHours( hours, minutes, 0, 0 );

        return result;
    };
    const isoDay = ( date: Date ): number => ( date.getDay() === 0 ? 7 : date.getDay() );

    if( kind === "monthly_first" ) {
        const firstOf = ( year: number, month: number ): Date => {
            const date = new Date( year, month, 1 );

            date.setDate( 1 + ( ( weekday - isoDay( date ) + 7 ) % 7 ) );

            return at( date );
        };
        const current = firstOf( from.getFullYear(), from.getMonth() );

        return current.getTime() > from.getTime()
            ? current
            : firstOf( from.getFullYear(), from.getMonth() + 1 );
    }

    const date = at( from );

    date.setDate( date.getDate() + ( ( weekday - isoDay( date ) + 7 ) % 7 ) );

    if( date.getTime() <= from.getTime() )
        date.setDate( date.getDate() + 7 );

    return date;
}
